import api from 'common/api'
import { FileDeliveryOptions, Integrations } from 'store/college'

import { CampusESPConfiguration } from './integrationsReducer'

export const updateConfiguredDeliveryMethod = async ({
  scid,
  deliveryMethod,
}: {
  scid: number
  deliveryMethod: 'sftp' | 'slate'
}) => {
  return api.post(`/api/colleges/${scid}/integrations/configured_delivery_method`, {
    configuredDeliveryMethod: deliveryMethod,
  })
}
export const saveSlateDomain = async ({ scid, domain }: { scid: number; domain: string }) => {
  return api.post<{ Subdomain: string }, Integrations>(`/api/colleges/${scid}/integrations/slate/config`, {
    Subdomain: domain,
  })
}

export const testConnection = async ({ scid, type }: { scid: number; type: 'slate' | 'sftp' }) => {
  return api.get(`/api/colleges/${scid}/integrations/${type}/test`)
}

export const updateDocsIntegrationStatus = async ({
  scid,
  activate,
  type,
}: {
  scid: number
  activate: boolean
  type: 'slate' | 'sftp'
}) => {
  const activationRoute = `/api/colleges/${scid}/integrations/${type}/activate`
  if (activate) {
    return api.post<object, Integrations>(activationRoute, {})
  } else {
    return api.delete<Integrations>(activationRoute)
  }
}

export const updateHandraiserToggle = async ({ scid, enabled }: { scid: number; enabled: boolean }) => {
  return api.post<{ handraiserEnabled: boolean }, FileDeliveryOptions>(
    `/api/colleges/${scid}/integrations/handraisers/toggle`,
    { handraiserEnabled: enabled },
  )
}

export const updateSendParentHandraisersBeforeStudentCheckbox = async ({
  scid,
  sendParentHandraisersBeforeStudent,
}: {
  scid: number
  sendParentHandraisersBeforeStudent: boolean
}) => {
  return api.post<{ sendParentHandraisersBeforeStudent: boolean }, FileDeliveryOptions>(
    `/api/colleges/${scid}/integrations/handraisers/sendParentHandraisersBeforeStudent`,
    { sendParentHandraisersBeforeStudent: sendParentHandraisersBeforeStudent },
  )
}

/** MANUAL SETUP */

type ServerLocationBody = {
  Host: string
  Port: number
  Path?: string
}
export const saveServerLocation = async ({
  scid,
  hostname,
  port,
  path,
}: {
  scid: number
  hostname: string
  port: number
  path?: string
}) => {
  return api.post<ServerLocationBody, Integrations>(`/api/colleges/${scid}/integrations/sftp/location`, {
    Host: hostname,
    Port: port,
    Path: path,
  })
}

type ServerCredentialsBody = {
  Username: string
  Password?: string
}
export const saveServerCredentials = async ({
  scid,
  username,
  password,
}: {
  scid: number
  username: string
  password?: string
}) => {
  return api.post<ServerCredentialsBody, Integrations>(`/api/colleges/${scid}/integrations/sftp/credentials`, {
    Username: username,
    Password: password,
  })
}

export const generateSftpKeyPair = async ({ scid }: { scid: number }) => {
  return api.post<object, Integrations>(`/api/colleges/${scid}/integrations/sftp/keypair`, {})
}

export const removeSftpKeyPair = async ({ scid }: { scid: number }) => {
  return api.delete<Integrations>(`/api/colleges/${scid}/integrations/sftp/keypair`)
}

export const updateBatchTranscriptSetting = async ({ scid, isBatched }: { scid: number; isBatched: boolean }) => {
  const route = `/api/colleges/${scid}/integrations/batch/concatenatetranscripts`
  if (isBatched) {
    return api.put(route, {})
  } else {
    return api.delete(route)
  }
}

// To be deprecated - As of 02/2026 - 16 schools left
// isDownload = 12 schools
// isSFTPActive but not Slate = 3 schools
export const forceNewEasySlateIndexFields = async ({ scid }: { scid: number }) => {
  return api.put(`/api/colleges/${scid}/integrations/batch/neweasyslateindex`, {})
}

/** CampusESP Integration */

type CampusESPConfigurationResponse = {
  configurations: CampusESPConfiguration[]
}

export const getSFTPConfigurations = async () => {
  return api.get<CampusESPConfigurationResponse>(`/api/sftp`)
}

export type CreateSFTPConfigurationParams = {
  type: 'campusESP'
  username: string
}

export const createSFTPConfiguration = async ({ type = 'campusESP', username }: CreateSFTPConfigurationParams) => {
  return api.post<CreateSFTPConfigurationParams, CampusESPConfiguration>(`/api/sftp`, { type, username })
}

export type UpdateCampusESPConfiguration = {
  id: string
  username?: string
  enabled?: boolean
}

export const updateSFTPConfiguration = async ({ id, username, enabled }: UpdateCampusESPConfiguration) => {
  return api.put<Omit<UpdateCampusESPConfiguration, 'id'>, CampusESPConfiguration>(`/api/sftp/${id}`, {
    username,
    enabled,
  })
}

export const regenerateSFTPKey = async ({ id }: { id: string }) => {
  return api.put<null, CampusESPConfiguration>(`/api/sftp/${id}/key-gen`)
}

export const testSFTPConfiguration = async ({ id }: { id: string }) => {
  return api.get(`/api/sftp/${id}/test`)
}
